/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef } from "react";

type Fn = (...args: any[]) => any;
type ThrottleOptions = { leading?: boolean; trailing?: boolean };

export function useThrottle<T extends Fn>(
  callback: T,
  delay: number,
  options: ThrottleOptions = {}
): T & { cancel: () => void; flush: () => void } {
  const { leading = true, trailing = true } = options;

  const cbRef = useRef(callback);
  const lastExecRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastArgsRef = useRef<Parameters<T> | null>(null);

  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  const clearTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const invoke = (args: Parameters<T>) => {
    lastExecRef.current = Date.now();
    // Validación de seguridad antes de invocar el callback
    if (cbRef.current) {
      cbRef.current(...args);
    }
  };

  const throttled = useCallback(
    ((...args: Parameters<T>) => {
      const now = Date.now();
      const timeSinceLast = now - lastExecRef.current;

      if (lastExecRef.current === 0 && !leading) {
        // primer golpe, leading=false → NO ejecutar ahora, sólo abrir ventana
        lastExecRef.current = now;
        if (trailing) {
          lastArgsRef.current = args;
          clearTimer();
          timeoutRef.current = setTimeout(() => {
            if (lastArgsRef.current) invoke(lastArgsRef.current);
            lastArgsRef.current = null;
          }, delay);
        }
        return;
      }

      if (timeSinceLast >= delay) {
        clearTimer();
        if (leading || lastExecRef.current !== 0) {
          invoke(args);
        } else if (trailing) {
          lastArgsRef.current = args;
          timeoutRef.current = setTimeout(() => {
            if (lastArgsRef.current) invoke(lastArgsRef.current);
            lastArgsRef.current = null;
          }, delay);
        }
      } else if (trailing) {
        lastArgsRef.current = args;
        clearTimer();
        timeoutRef.current = setTimeout(() => {
          if (lastArgsRef.current) invoke(lastArgsRef.current);
          lastArgsRef.current = null;
        }, delay - timeSinceLast);
      }
    }) as T,
    [delay, leading, trailing]
  ) as T & { cancel: () => void; flush: () => void };

  throttled.cancel = () => {
    lastArgsRef.current = null;
    clearTimer();
  };

  throttled.flush = () => {
    if (lastArgsRef.current) {
      clearTimer();
      invoke(lastArgsRef.current);
      lastArgsRef.current = null;
    }
  };

  useEffect(() => clearTimer, []);

  return throttled;
}

/**
 * Hook para debouncing de funciones
 * Retrasa la ejecución hasta que no se llame durante el delay especificado
 */
type DebounceOptions = { leading?: boolean; trailing?: boolean };

export function useDebounce<T extends Fn>(
  callback: T,
  delay: number,
  options: DebounceOptions = {}
): T & { cancel: () => void; flush: () => void } {
  const { leading = false, trailing = true } = options;

  const cbRef = useRef(callback);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firedLeadingRef = useRef(false);
  const lastArgsRef = useRef<Parameters<T> | null>(null);

  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  const clearTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const debounced = useCallback(
    ((...args: Parameters<T>) => {
      lastArgsRef.current = args;

      if (leading && !timeoutRef.current && !firedLeadingRef.current) {
        cbRef.current(...args);
        firedLeadingRef.current = true;
      }

      clearTimer();

      if (trailing) {
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          if (trailing && (!leading || firedLeadingRef.current)) {
            if (lastArgsRef.current) cbRef.current(...lastArgsRef.current);
          }
          firedLeadingRef.current = false;
          lastArgsRef.current = null;
        }, delay);
      } else if (leading) {
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          firedLeadingRef.current = false;
        }, delay);
      }
    }) as T,
    [delay, leading, trailing]
  ) as T & { cancel: () => void; flush: () => void };

  debounced.cancel = () => {
    clearTimer();
    firedLeadingRef.current = false;
    lastArgsRef.current = null;
  };

  debounced.flush = () => {
    if (timeoutRef.current && lastArgsRef.current) {
      clearTimer();
      cbRef.current(...lastArgsRef.current);
      firedLeadingRef.current = false;
      lastArgsRef.current = null;
    }
  };

  useEffect(() => clearTimer, []);
  return debounced;
}
